// Insider Trading dashboard (/dashboard/insider-trading).
//
// Top-20 most liquid (highest average daily dollar volume) symbols with SEC
// Form-4 open-market insider Purchases and Sales over a filing-date window,
// served from GET /api/v1/insider-trading/top-liquid (pre-mirrored DB data, no
// live FMP call). Selecting a symbol (or looking one up) opens the live
// quarterly statistics + recent filings panel.

import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { Activity, CalendarDays, Info, RefreshCw, Search, TrendingDown, TrendingUp } from 'lucide-react';

import { Layout, Sidebar } from '../../shared/ui';
import { useTopLiquidInsiders } from './api/insiderTradingApi';
import { InsiderSymbolPanel } from './components/InsiderSymbolPanel';
import { InsiderTradesTable } from './components/InsiderTradesTable';
import { InsiderValueChart } from './components/InsiderValueChart';
import { cn, formatCompactUsd, formatDate } from './utils/formatters';

const WINDOWS = [
    { days: 1, label: '1D' },
    { days: 7, label: '7D' },
    { days: 14, label: '14D' },
    { days: 30, label: '30D' },
];

const SYMBOL_RE = /^[A-Za-z0-9.\-^]{1,20}$/;

const KpiCard = ({
    label,
    value,
    hint,
    icon,
    tone,
}: {
    label: string;
    value: string;
    hint?: string;
    icon: ReactNode;
    tone: 'emerald' | 'rose' | 'indigo' | 'slate';
}) => {
    const toneCls = {
        emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20',
        rose: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20',
        indigo: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20',
        slate: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800',
    }[tone];
    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm flex items-start gap-3">
            <div className={cn('p-2 rounded-lg', toneCls)}>{icon}</div>
            <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
                <div className="text-xl font-black text-slate-900 dark:text-white font-mono">{value}</div>
                {hint && <div className="text-xs text-slate-500 truncate">{hint}</div>}
            </div>
        </div>
    );
};

export const InsiderTradingPage = () => {
    const [days, setDays] = useState(7);
    const [filingDate, setFilingDate] = useState('');
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
    const [lookup, setLookup] = useState('');
    const panelRef = useRef<HTMLDivElement>(null);

    const { data, isLoading, isError, isFetching, refetch } = useTopLiquidInsiders(
        days,
        filingDate || undefined,
    );

    const handleSelect = useCallback((symbol: string) => setSelectedSymbol(symbol), []);

    useEffect(() => {
        if (selectedSymbol) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [selectedSymbol]);

    const handleLookup = (e: FormEvent) => {
        e.preventDefault();
        const s = lookup.trim().toUpperCase();
        if (SYMBOL_RE.test(s)) setSelectedSymbol(s);
    };

    const purchases = data?.purchases ?? [];
    const sales = data?.sales ?? [];
    const buyValue = purchases.reduce((acc, r) => acc + r.total_value, 0);
    const sellValue = sales.reduce((acc, r) => acc + r.total_value, 0);
    const windowLabel =
        data?.window_from && data?.window_to
            ? data.window_from === data.window_to
                ? formatDate(data.window_to)
                : `${formatDate(data.window_from)} – ${formatDate(data.window_to)}`
            : '—';

    return (
        <Layout sidebar={<Sidebar />} title="Insider Trading">
            <div className="space-y-6 text-slate-900 dark:text-slate-100 pb-12">
                {/* Header */}
                <header className="flex flex-col xl:flex-row xl:items-end justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
                    <div>
                        <h1 className="text-2xl font-black tracking-tight">Insider Trading Radar</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
                            The 20 most liquid names (by average daily dollar volume) where corporate
                            insiders filed open-market <span className="font-semibold text-emerald-600">purchases</span>{' '}
                            or <span className="font-semibold text-rose-600">sales</span> (SEC Form 4).
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-0.5">
                            {WINDOWS.map((w) => (
                                <button
                                    key={w.days}
                                    type="button"
                                    onClick={() => {
                                        setDays(w.days);
                                        setFilingDate('');
                                    }}
                                    className={cn(
                                        'px-3 py-1.5 text-xs font-bold rounded-md transition-colors',
                                        !filingDate && days === w.days
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                                    )}
                                >
                                    {w.label}
                                </button>
                            ))}
                        </div>
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                            <CalendarDays className="w-4 h-4" />
                            <input
                                type="date"
                                value={filingDate}
                                max={data?.latest_available_filing_date ?? undefined}
                                onChange={(e) => setFilingDate(e.target.value)}
                                className="px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                                aria-label="Exact filing date"
                            />
                        </label>
                        <form onSubmit={handleLookup} className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                value={lookup}
                                onChange={(e) => setLookup(e.target.value)}
                                placeholder="Look up symbol…"
                                className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 w-40 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                aria-label="Look up insider statistics for a symbol"
                            />
                        </form>
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                            aria-label="Refresh"
                        >
                            <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
                        </button>
                    </div>
                </header>

                {isError && (
                    <div className="p-4 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800">
                        <h3 className="font-bold">Data connection failed</h3>
                        <p className="text-sm mt-1">Could not load the insider trading board. Please retry.</p>
                    </div>
                )}

                {/* KPIs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    <KpiCard
                        label="Filing Window"
                        value={windowLabel}
                        hint={
                            data?.latest_available_filing_date
                                ? `Latest filing: ${formatDate(data.latest_available_filing_date)}`
                                : undefined
                        }
                        icon={<CalendarDays className="w-5 h-5" />}
                        tone="slate"
                    />
                    <KpiCard
                        label="Top-20 Purchase Value"
                        value={formatCompactUsd(buyValue)}
                        hint={`${data?.purchase_symbols_considered ?? 0} symbols with buys`}
                        icon={<TrendingUp className="w-5 h-5" />}
                        tone="emerald"
                    />
                    <KpiCard
                        label="Top-20 Sale Value"
                        value={formatCompactUsd(sellValue)}
                        hint={`${data?.sale_symbols_considered ?? 0} symbols with sales`}
                        icon={<TrendingDown className="w-5 h-5" />}
                        tone="rose"
                    />
                    <KpiCard
                        label="Buy / Sell Ratio"
                        value={sellValue > 0 ? (buyValue / sellValue).toFixed(2) : '—'}
                        hint={data ? `Computed in ${data.execution_time_ms.toFixed(0)} ms` : undefined}
                        icon={<Activity className="w-5 h-5" />}
                        tone="indigo"
                    />
                </div>

                {selectedSymbol && (
                    <div ref={panelRef} className="scroll-mt-4">
                        <InsiderSymbolPanel symbol={selectedSymbol} onClose={() => setSelectedSymbol(null)} />
                    </div>
                )}

                <InsiderTradesTable
                    title="Top 20 · Insider Purchases"
                    subtitle="Transaction type P-Purchase · ranked by average daily dollar volume"
                    rows={purchases}
                    theme="buy"
                    isLoading={isLoading}
                    selectedSymbol={selectedSymbol}
                    onSelectSymbol={handleSelect}
                />

                <InsiderTradesTable
                    title="Top 20 · Insider Sales"
                    subtitle="Transaction type S-Sale · ranked by average daily dollar volume"
                    rows={sales}
                    theme="sell"
                    isLoading={isLoading}
                    selectedSymbol={selectedSymbol}
                    onSelectSymbol={handleSelect}
                />

                <InsiderValueChart purchases={purchases} sales={sales} />

                <p className="flex items-start gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    Source: SEC Form 4, refreshed twice daily. Only open-market
                    purchases (P) and sales (S) with a positive price are included; awards, gifts and option
                    exercises are excluded. Names priced under $1 or trading under $1M per day are filtered out to support
                    Forge Ultra high-quality investment universe focus.
                    “Value / ADV” compares insider dollar value to one average trading day.
                </p>
            </div>
        </Layout>
    );
};

export default InsiderTradingPage;
